import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output, signal } from '@angular/core';

/** A choice in a dropdown filter: the value applied and its display label. */
export interface FilterMenuOption {
  value: string;
  label: string;
}

/** A filter picked from a dropdown of options; its value is stored under `key`. */
export interface FilterMenuSelectField {
  kind: 'select';
  key: string;
  label: string;
  options: FilterMenuOption[];
}

/**
 * A from–to date filter; the days (`YYYY-MM-DD`) are stored under `fromKey` and
 * `toKey`, and either end may be left open.
 */
export interface FilterMenuDateRangeField {
  kind: 'dateRange';
  key: string;
  label: string;
  fromKey: string;
  toKey: string;
}

export type FilterMenuField = FilterMenuSelectField | FilterMenuDateRangeField;

/** An applied filter as a chip: which field, and its value in words. */
interface AppliedFilterChip {
  field: FilterMenuField;
  valueLabel: string;
}

/** Fixed-position coordinates for the open panel. */
interface PanelPosition {
  top: number;
  left: number;
}

/**
 * A "Filters" button that opens a menu of the available filters: pick one, set how
 * it applies (an option, or a date range), and Apply. Applied filters show beside
 * the button as chips with their values — click a chip to change it, or its × to
 * remove it.
 *
 * Presentational: the host passes the filter definitions and the current values
 * (a key → value map) and receives the whole new map on every change.
 *
 * @example
 * <fb-filter-menu [fields]="filterFields" [values]="filters()" (valuesChange)="filters.set($event)" />
 */
@Component({
  selector: 'fb-filter-menu',
  templateUrl: './filter-menu.html',
  styleUrl: './filter-menu.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'close()',
    '(window:resize)': 'close()',
  },
})
export class FilterMenu {
  /** The filters on offer, in menu order. */
  readonly fields = input<FilterMenuField[]>([]);

  /** The applied values, keyed by each field's `key` (or a date range's `fromKey` / `toKey`). */
  readonly values = input<Readonly<Record<string, string | undefined>>>({});

  /** Emitted with the complete new values after a filter is applied or removed. */
  readonly valuesChange = output<Record<string, string>>();

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  private static readonly PANEL_WIDTH = 260;

  protected readonly isOpen = signal(false);
  protected readonly panelPosition = signal<PanelPosition | null>(null);
  /** The filter being set; null shows the list of filters. */
  protected readonly editingField = signal<FilterMenuField | null>(null);

  /** The value being chosen for a dropdown filter, before Apply. */
  protected readonly draftValue = signal('');
  /** The days being chosen for a date range, before Apply. */
  protected readonly draftFrom = signal('');
  protected readonly draftTo = signal('');

  /** Apply needs a value: an option, or at least one end of a date range. */
  protected readonly canApply = computed(() => {
    const editingField = this.editingField();
    if (editingField === null) {
      return false;
    }
    return editingField.kind === 'select' ? this.draftValue() !== '' : this.draftFrom() !== '' || this.draftTo() !== '';
  });

  /** One chip per applied filter, in the fields' order. */
  protected readonly appliedChips = computed<AppliedFilterChip[]>(() => {
    const currentValues = this.values();
    const chips: AppliedFilterChip[] = [];
    for (const field of this.fields()) {
      if (field.kind === 'select') {
        const appliedValue = currentValues[field.key];
        if (appliedValue) {
          const matchingOption = field.options.find((option) => option.value === appliedValue);
          chips.push({ field, valueLabel: matchingOption?.label ?? appliedValue });
        }
      } else {
        const fromDay = currentValues[field.fromKey];
        const toDay = currentValues[field.toKey];
        if (fromDay || toDay) {
          const rangeLabel = fromDay && toDay ? `${fromDay} – ${toDay}` : fromDay ? `from ${fromDay}` : `until ${toDay}`;
          chips.push({ field, valueLabel: rangeLabel });
        }
      }
    }
    return chips;
  });

  /** Opens the menu on the list of filters, or closes it. */
  protected toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.openPanel(null);
    }
  }

  protected close(): void {
    this.isOpen.set(false);
    this.editingField.set(null);
  }

  /** Step two: how the chosen filter applies, starting from its current value. */
  protected editField(field: FilterMenuField): void {
    const currentValues = this.values();
    if (field.kind === 'select') {
      this.draftValue.set(currentValues[field.key] ?? '');
    } else {
      this.draftFrom.set(currentValues[field.fromKey] ?? '');
      this.draftTo.set(currentValues[field.toKey] ?? '');
    }
    this.editingField.set(field);
  }

  /** Back from a filter's settings to the list of filters. */
  protected backToFields(): void {
    this.editingField.set(null);
  }

  /** A chip reopens the menu on its filter. */
  protected editChip(field: FilterMenuField): void {
    this.openPanel(field);
  }

  protected apply(): void {
    const editingField = this.editingField();
    if (editingField === null || !this.canApply()) {
      return;
    }
    const nextValues = this.withoutField(editingField);
    if (editingField.kind === 'select') {
      nextValues[editingField.key] = this.draftValue();
    } else {
      if (this.draftFrom()) {
        nextValues[editingField.fromKey] = this.draftFrom();
      }
      if (this.draftTo()) {
        nextValues[editingField.toKey] = this.draftTo();
      }
    }
    this.valuesChange.emit(nextValues);
    this.close();
  }

  /** A chip's ×: drops that filter. */
  protected remove(field: FilterMenuField): void {
    this.valuesChange.emit(this.withoutField(field));
  }

  /** The current values minus a field's own (both ends for a date range), empty ones dropped. */
  private withoutField(field: FilterMenuField): Record<string, string> {
    const removedKeys = field.kind === 'select' ? [field.key] : [field.fromKey, field.toKey];
    const remainingValues: Record<string, string> = {};
    for (const [valueKey, appliedValue] of Object.entries(this.values())) {
      if (appliedValue && !removedKeys.includes(valueKey)) {
        remainingValues[valueKey] = appliedValue;
      }
    }
    return remainingValues;
  }

  /** Anchors the fixed panel under the Filters button, kept inside the viewport. */
  private openPanel(field: FilterMenuField | null): void {
    const filtersButton = this.elementRef.nativeElement.querySelector('.fm-button');
    if (filtersButton && typeof window !== 'undefined') {
      const buttonRect = filtersButton.getBoundingClientRect();
      const left = Math.max(8, Math.min(buttonRect.left, window.innerWidth - FilterMenu.PANEL_WIDTH - 8));
      this.panelPosition.set({ top: Math.round(buttonRect.bottom + 4), left: Math.round(left) });
    }
    if (field === null) {
      this.editingField.set(null);
    } else {
      this.editField(field);
    }
    this.isOpen.set(true);
  }
}
